import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, CheckCircle2, HardHat, Play, Plus, UserCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

const WO_LABEL = { draft: "Draft", assigned: "Assigned", in_progress: "In Progress", on_hold: "On Hold", completed: "Completed", cancelled: "Cancelled" };
const NEXT = { draft: ["assigned", "cancelled"], assigned: ["in_progress", "cancelled"], in_progress: ["on_hold", "completed"], on_hold: ["in_progress", "cancelled"] };

export default function WorkOrders() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [parties, setParties] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ party_id: "", project: "", site: "", description: "", employee_id: "",
    supervisor: "", start_date: "", expected_completion: "", estimated_cost: 0 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/work-orders?limit=200").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line

  useEffect(() => {
    if (!dialog) return;
    api.get("/parties?limit=200").then((r) => setParties(r.data.items.filter((p) => !p.archived))).catch(() => {});
    api.get("/employees?limit=300").then((r) => setEmployees(r.data.items)).catch(() => setEmployees([]));
  }, [dialog]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.party_id || !form.description.trim()) { toast.error("Client and work description are required"); return; }
    setBusy(true);
    try {
      await api.post("/work-orders", { ...form, estimated_cost: +form.estimated_cost || 0 });
      toast.success("Work order created");
      setDialog(false);
      setForm({ party_id: "", project: "", site: "", description: "", employee_id: "", supervisor: "", start_date: "", expected_completion: "", estimated_cost: 0 });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const advance = async (wo, status) => {
    try {
      await api.post(`/work-orders/${wo.id}/status`, { status });
      toast.success(`${wo.doc_no} → ${WO_LABEL[status]}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ICONS = { assigned: UserCheck, in_progress: Play, on_hold: Ban, completed: CheckCircle2, cancelled: Ban };

  return (
    <div data-testid="work-orders-page">
      <PageHeader title="Work Orders" testId="work-orders-title" sub="Site work assignment and tracking">
        <Button data-testid="new-wo-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Work Order</Button>
      </PageHeader>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="wo-empty" title="No work orders" body="Assign site work to employees and track progress to completion." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="wo-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">WO No</th><th className="p-3">Client / Project</th><th className="p-3">Work</th>
                <th className="p-3">Assigned To</th><th className="p-3">Expected</th><th className="p-3 text-right">Est. Cost</th>
                <th className="p-3">Status</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((wo) => (
                  <tr key={wo.id} data-testid={`wo-row-${wo.doc_no}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs font-semibold">{wo.doc_no}</td>
                    <td className="p-3"><p className="font-medium max-w-[180px] truncate">{wo.party_name}</p>
                      <p className="text-xs text-slate-500 truncate">{[wo.project, wo.site].filter(Boolean).join(" · ")}</p></td>
                    <td className="p-3 text-xs text-slate-500 max-w-[200px] truncate">{wo.description}</td>
                    <td className="p-3 text-slate-600 dark:text-slate-300">{wo.employee_name || "—"}</td>
                    <td className="p-3">{fmtDate(wo.expected_completion)}</td>
                    <td className="p-3 text-right"><Money value={wo.estimated_cost} /></td>
                    <td className="p-3"><StatusBadge status={WO_LABEL[wo.status]} /></td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        {(NEXT[wo.status] || []).map((s) => {
                          const Icon = ICONS[s];
                          return (
                            <Button key={s} size="sm" variant={s === "cancelled" ? "ghost" : "outline"}
                              data-testid={`wo-${s}-${wo.doc_no}`} onClick={() => advance(wo, s)}
                              className={`h-7 text-xs ${s === "cancelled" ? "text-red-500" : ""}`}>
                              <Icon className="mr-1 h-3 w-3" />{WO_LABEL[s]}
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="wo-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><HardHat className="h-5 w-5 text-amber-500" /> New Work Order</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Client *</Label>
              <Select value={form.party_id} onValueChange={(v) => set("party_id", v)}>
                <SelectTrigger data-testid="wo-client" className="mt-1.5"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Project</Label><Input className="mt-1.5" value={form.project} onChange={(e) => set("project", e.target.value)} placeholder="Project name" /></div>
            <div className="col-span-2"><Label>Work Description *</Label><Textarea data-testid="wo-description" rows={2} className="mt-1.5" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Scope of work..." /></div>
            <div><Label>Site</Label><Input className="mt-1.5" value={form.site} onChange={(e) => set("site", e.target.value)} placeholder="Site / location" /></div>
            <div><Label>Assigned Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => set("employee_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="wo-employee" className="mt-1.5"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned —</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Supervisor</Label><Input className="mt-1.5" value={form.supervisor} onChange={(e) => set("supervisor", e.target.value)} /></div>
            <div><Label>Estimated Cost (₹)</Label><Input type="number" min="0" className="mt-1.5" value={form.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} /></div>
            <div><Label>Start Date</Label><Input type="date" className="mt-1.5" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div><Label>Expected Completion</Label><Input type="date" className="mt-1.5" value={form.expected_completion} onChange={(e) => set("expected_completion", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="wo-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Create Work Order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
